import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";
import { getAuthHeaders, handleApiResponse } from "../utils/apiHelpers";

/** Per-question verdict from the MN judge assessment. */
export interface MnJudgeAnswerAssessment {
  q_idx: number;
  verdict: string;
  criterion?: string;
  note?: string;
  question?: string | null;
  answer?: string | null;
}

/** Criteria block returned with an MN judge assessment. */
export interface MnJudgeCriteria {
  source?: string;
  rxcui?: string | null;
  resolution?: string;
  source_id?: string | null;
  text?: Record<string, string>;
}

/** Latest MN judge verdict payload (StoredAssessment.latest). */
export interface MnJudgeLatestAssessment {
  identifier: string;
  decision: string;
  confidence?: number;
  confidence_basis?: string;
  rationale?: string;
  answer_assessment?: MnJudgeAnswerAssessment[];
  recommended_action?: string;
  criteria?: MnJudgeCriteria;
  mined_insight?: {
    reference_only?: boolean;
    drug_form?: Record<string, unknown> | null;
    drug_plan?: Record<string, unknown> | null;
  } | null;
  answer_source?: string;
  attempt?: number;
  answers_fingerprint?: string;
  answered_count?: number;
  model?: string;
  assessed_at?: string;
}

/**
 * Response from GET /pharmacy/mn-judge/assessment/{identifier}.
 */
export interface MnJudgeStoredAssessment {
  identifier: string;
  latest: MnJudgeLatestAssessment | null;
  attempts?: MnJudgeLatestAssessment[];
}

/**
 * Fetches the stored MN judge assessment for a PA case identifier.
 *
 * @param identifier - PA case identifier
 * @returns Stored assessment with latest verdict and attempt history
 * @throws Error when the request fails or identifier is empty
 */
export const fetchMnJudgeAssessment = async (
  identifier: string,
): Promise<MnJudgeStoredAssessment> => {
  if (!identifier) {
    throw new Error("identifier is required to fetch MN judge assessment");
  }

  const url = `${API_ENDPOINTS.MN_JUDGE_ASSESSMENT}/${encodeURIComponent(identifier)}`;
  const authHeaders = await getAuthHeaders();

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { ...authHeaders },
    });
    const data = (await handleApiResponse(response)) as MnJudgeStoredAssessment;
    logDataToConsole("MN judge assessment", data);
    return data;
  } catch (error) {
    logError(error as Error, "Error in fetchMnJudgeAssessment");
    throw error;
  }
};
